import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorMessage, apiFieldErrors } from "../services/api";
import { uploadBranchMedia } from "../services/branchService";

/**
 * Adds photos and videos to a branch on their own — the branch's details are
 * never part of the request.
 */
export default function UploadMediaModal({ branchId, open, onClose, onUploaded }) {
  const { t } = useTranslation();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  // Start from a clean slate every time the modal opens.
  useEffect(() => {
    if (open) {
      setFiles([]);
      setError("");
      if (inputRef.current) inputRef.current.value = "";
    }
  }, [open]);

  // Escape closes, and the page behind must not scroll while it is open.
  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => {
      if (e.key === "Escape" && !uploading) onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose, uploading]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    if (files.length === 0) {
      setError(t("media.chooseAtLeastOne"));
      return;
    }

    setUploading(true);
    try {
      const media = await uploadBranchMedia(branchId, files);
      onUploaded(media);
      onClose();
    } catch (err) {
      const fieldErrors = apiFieldErrors(err);
      const first = Object.values(fieldErrors).flat()[0];
      setError(first || apiErrorMessage(err, t("media.uploadFailed")));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !uploading) onClose();
      }}
    >
      <div role="dialog" aria-modal="true" className="w-full max-w-md rounded-xl bg-surface shadow-xl">
        <form onSubmit={submit}>
          <div className="flex items-center justify-between border-b border-line px-5 py-3">
            <h2 className="text-sm font-semibold">{t("media.modalTitle")}</h2>
            <button
              type="button"
              onClick={onClose}
              disabled={uploading}
              aria-label={t("common.close")}
              className="rounded p-1 text-ink-muted hover:bg-raised hover:text-ink-2"
            >
              ✕
            </button>
          </div>

          <div className="px-5 py-4">
            {error && (
              <div className="mb-3 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
                {error}
              </div>
            )}

            <label className="field-label" htmlFor="new-media">
              {t("branchForm.media")}
            </label>
            <input
              ref={inputRef}
              id="new-media"
              type="file"
              multiple
              disabled={uploading}
              accept=".jpg,.jpeg,.png,.webp,.mp4,.mov,.webm"
              className="field-input"
              onChange={(e) => setFiles(Array.from(e.target.files))}
            />
            <p className="mt-1 text-xs text-ink-2">
              {t("branchForm.mediaHint")}
            </p>

            {files.length > 0 && (
              <ul className="mt-3 max-h-32 space-y-1 overflow-y-auto text-xs text-ink-2">
                {files.map((file) => (
                  <li key={file.name} className="flex justify-between gap-2">
                    <span className="truncate">{file.name}</span>
                    <span className="flex-shrink-0 text-ink-2">
                      {(file.size / 1024 / 1024).toFixed(1)} MB
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex justify-end gap-2 border-t border-line px-5 py-3">
            <button type="button" onClick={onClose} disabled={uploading} className="btn-secondary">
              {t("common.cancel")}
            </button>
            <button type="submit" disabled={uploading} className="btn-primary">
              {uploading ? t("media.uploading") : `${t("media.upload")}${files.length ? ` (${files.length})` : ""}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
